import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ReactHelmet from '../components/SEO';
import { FaLeaf, FaTractor, FaUsers, FaChartLine, FaHandsHelping, FaSeedling } from 'react-icons/fa';
import heroImage from '../assets/hero.webp';
import about1 from '../assets/about1.webp';
import about2 from '../assets/about2.webp';
import about3 from '../assets/about3.webp';
import about4 from '../assets/about4.webp';
import about5 from '../assets/about5.webp';


const About = () => {
  const stats = [
    {
      value: '10,000+',
      label: 'खुशहाल किसान',
      icon: <FaUsers className="text-4xl" />,
      description: 'हमारे साथ जुड़कर जिन किसानों ने अपनी आमदनी बढ़ाई और आधुनिक खेती सीखी'
    },
    {
      value: '500+',
      label: 'गाँवों में पहुँच',
      icon: <FaLeaf className="text-4xl" />,
      description: 'जहाँ हमारे किसान भाई अब कम लागत में बेहतर फसल उगा रहे हैं'
    },
    {
      value: '75%',
      label: 'फसल बढ़ोतरी',
      icon: <FaChartLine className="text-4xl" />,
      description: 'हमारे तरीके अपनाने वाले किसानों की औसत उपज में हुई वृद्धि'
    },
    {
      value: '15 वर्ष',
      label: 'किसानों का साथ',
      icon: <FaTractor className="text-4xl" />,
      description: 'हम किसानों के लिए नई तकनीक और जानकारी लाते आ रहे हैं'
    },
  ];

  const timeline = [
    {
      year: '2008',
      title: 'शुरुआत',
      description: 'महाराष्ट्र के 15 किसानों के साथ छोटी सी शुरुआत'
    },
    {
      year: '2012',
      title: 'पहली बड़ी सफलता',
      description: '1000 किसानों तक पहुँच, जैविक खेती में मिली अच्छी पैदावार'
    },
    {
      year: '2015',
      title: 'मोबाइल तकनीक',
      description: 'किसानों के लिए खेती की जानकारी देने वाला ऐप लॉन्च'
    },
    {
      year: '2018',
      title: '5 नए राज्य',
      description: 'मध्य प्रदेश, उत्तर प्रदेश, राजस्थान समेत 5 राज्यों में विस्तार'
    },
    {
      year: '2022',
      title: 'अंतरराष्ट्रीय पहचान',
      description: 'हमारे काम को संयुक्त राष्ट्र द्वारा सम्मानित किया गया'
    },
    {
      year: '2026',
      title: 'नया लक्ष्य',
      description: '1 लाख किसानों तक पहुँचने का संकल्प'
    }
  ];

  return (
    <>
      <ReactHelmet
        title="Unnatkheti के बारे में - किसानों का सच्चा साथी"
        metaDesc="Unnatkheti - जहाँ हर किसान को मिलती है आधुनिक खेती की सही जानकारी और तकनीक"
        metaKeywords="unnatkheti, किसान सहायता, खेती की जानकारी, जैविक खेती, किसान ऐप, agribusiness ideas, agriculture business plan

farming business ideas

agri startup ideas

agriculture market trends

crop marketing strategies

agricultural products suppliers

farm equipment business

agriculture export opportunities

value addition in agriculture, Kheti, kheti"
      />
      <div className="h-20"></div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-700 to-emerald-900 text-white py-28 min-h-[80vh]">
        <div className="absolute inset-0 bg-black/30 h-full">
          <img
            src={heroImage}
            alt="खेत में काम करते किसान , agribusiness ideas, agriculture business plan, farming business ideas"
            className="w-full h-full object-cover object-center"
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="container mx-auto px-4 relative z-10 text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            किसानों की तरक्की, हमारी खुशी
          </h1>
          <p className="text-xl md:text-2xl max-w-2xl mx-auto">
            हम आपको सिखाते हैं कम पानी, कम खर्च में कैसे उगाएं बेहतर फसल
          </p>
        </motion.div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Our <span className="text-green-600">Mission</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              UnnatKheti का मकसद है छोटे और मध्यम किसानों की आय बढ़ाना और खेती को उनके लिए लाभ वाला काम बनाना।
            </p>
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 h-1 w-24 mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src={about1}
                alt="किसान खेत में काम करते हुए"
                className="rounded-xl shadow-xl hover:shadow-2xl transition-shadow duration-500"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-4">हमारी यात्रा</h3>
                <audio controls className="mb-6">
                      <source src="/audio/humari-yatra.mp3" type="audio/mp3" />

                   आपका ब्राउज़र ऑडियो को सपोर्ट नहीं करता।
                </audio> 
                

              <p className="text-gray-600 mb-6">
                हमारी शुरुआत 2010 में हुई थी, जब हमने देखा कि हमारे किसान कई मुश्किलों से गुजर रहे हैं — पुरानी खेती के तरीके, बदलता मौसम और बिचौलियों का शोषण। हमें लगा कि अगर हम मदद न करें, तो किसान और ज्यादा परेशान होंगे।
              </p>
              <p className="text-gray-600 mb-6">
                अब तक हमने 10,000 से ज्यादा किसानों को मदद की है और 200 से ज्यादा गांवों में नई और बेहतर खेती के तरीके पहुंचाए हैं। हमने किसानों की ज़िन्दगी में खुशियाँ लाई हैं।
              </p>
              <p className="text-gray-600 mb-6">
                हमारा सपना है कि 2030 तक 1 लाख किसानों को डिजिटल खेती के जरिये मजबूत बनाएं ताकि वे अपने परिवार और देश के लिए बेहतर भविष्य बना सकें।
              </p>
              <p className="text-gray-600 mb-6">
                अगर आप भी अपने खेत, परिवार और सपनों से जुड़े हैं, तो हम आपके साथ हैं। आइए, मिलकर खेती को फिर से सम्मान और सफलता दिलाएं। आपका साथ हमारे लिए सबसे बड़ी ताकत है।
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{
                  y: -10,
                  scale: 1.05,
                  boxShadow: "0 20px 25px -5px rgba(16, 185, 129, 0.2), 0 10px 10px -5px rgba(16, 185, 129, 0.1)"
                }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 text-center group border-2 border-transparent hover:border-emerald-200 relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-green-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"></div>

                <div className="relative z-10">
                  <div className="text-green-600 mb-6 flex justify-center group-hover:text-emerald-700 transition-colors duration-300 group-hover:scale-110">
                    {stat.icon}
                  </div>
                  <h3 className="text-4xl font-bold text-gray-800 mb-2 group-hover:text-emerald-800 transition-colors duration-300">
                    {stat.value}
                  </h3>
                  <p className="text-lg font-medium text-gray-700 mb-2 group-hover:text-emerald-700 transition-colors duration-300">
                    {stat.label}
                  </p>
                  <p className="text-gray-500 text-sm group-hover:text-gray-600 transition-colors duration-300 relative">
                    {stat.description}
                  </p>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-green-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              हमारी <span className="text-green-600">यात्रा</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              छोटी शुरुआत से लेकर बड़े बदलाव तक का हमारा सफर
            </p>
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 h-1 w-24 mx-auto"></div>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 h-full w-1 bg-green-100 transform -translate-x-1/2"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} items-center`}
                >
                  <div className="w-1/2 px-4 py-2 text-center">
                    <div className="inline-block bg-green-600 text-white text-lg font-bold px-6 py-2 rounded-full shadow-md">
                      {item.year}
                    </div>
                  </div>

                  <div className="w-1/2 px-4">
                    <div className={`p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 border-l-4 ${index % 2 === 0 ? 'border-green-500' : 'border-emerald-500'}`}>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>

                  <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-green-500 rounded-full border-4 border-white shadow-md"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              हमारा <span className="text-green-600">लक्ष्य</span> और सपना
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              हम चाहते हैं कि हर किसान खुशहाल हो और भारत की खेती दुनिया में सबसे आगे हो
            </p>
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 h-1 w-24 mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src={about4}
                alt="खेत में फसल"
                className="rounded-xl shadow-xl hover:shadow-2xl transition-shadow duration-500"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="bg-green-100 p-3 rounded-full mr-4">
                    <FaLeaf className="text-green-600 text-xl" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">प्राकृतिक खेती</h4>
                    <audio controls className="mb-6">
                      <source src="/audio/prakrutikkheti.mp3" type="audio/mp3" />

                   आपका ब्राउज़र ऑडियो को सपोर्ट नहीं करता।
                </audio> 
                    <p className="text-gray-600">
                      हम सिखाते हैं कि कैसे बिना महंगे रासायनिक खाद और कीटनाशकों के अच्छी फसल लें। जैविक खाद बनाना, जैविक कीटनाशक तैयार करना और मिट्टी को स्वस्थ रखना - ये सब हम आपको सिखाएँगे।
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-green-100 p-3 rounded-full mr-4">
                    <FaHandsHelping className="text-green-600 text-xl" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">किसान सशक्तिकरण</h4>
                    <audio controls className="mb-6">
                      <source src="/audio/ks.mp3" type="audio/mp3" />

                   आपका ब्राउज़र ऑडियो को सपोर्ट नहीं करता।
                </audio> 
                    <p className="text-gray-600">
                      हम किसानों को सिखाते हैं कि कैसे बाजार से सही दाम पाएँ, फसल बीमा करवाएँ और सरकारी योजनाओं का लाभ उठाएँ। हमारे विशेषज्ञ आपको हर कदम पर मार्गदर्शन देंगे।
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-green-100 p-3 rounded-full mr-4">
                    <FaSeedling className="text-green-600 text-xl" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">नवाचार</h4>
                       <audio controls className="mb-6">
                      <source src="/audio/navachar.mp3" type="audio/mp3" />

                   आपका ब्राउज़र ऑडियो को सपोर्ट नहीं करता।
                   </audio> 
                    <p className="text-gray-600">
                      हम किसानों को नई तकनीकों से जोड़ते हैं - ड्रिप सिंचाई, मल्चिंग, इंटरक्रॉपिंग जैसी आधुनिक विधियाँ जो पानी और मेहनत दोनों बचाती हैं। हमारा मोबाइल ऐप आपको दिन-प्रतिदिन की खेती की जानकारी देता रहेगा।
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 bg-gradient-to-b from-emerald-50 to-green-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              हम कैसे <span className="text-green-600">मदद</span> करते हैं
            </h2>
            <p className="text-lg text-gray-600">
              किसानों की हर समस्या का समाधान हमारे पास है
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{
                y: -10,
                scale: 1.03,
                backgroundColor: "#f0fdf4"
              }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-200"
            >
              <div className="h-48 bg-green-600 flex items-center justify-center relative overflow-hidden">
                <img
                  src={about2}
                  alt="किसान प्रशिक्षण"
                  className="w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-800/70 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">प्रशिक्षण कार्यक्रम</h3>
                <p className="text-gray-600">
                  हम गाँव-गाँव में जाकर किसानों को प्रशिक्षण देते हैं। मिट्टी की जाँच, बीज चयन, खाद प्रबंधन, सिंचाई तकनीक - हर विषय पर हाथों-हाथ सिखाया जाता है। हमारे प्रशिक्षक आपकी भाषा में समझाते हैं।
                </p>
              </div>
               <audio controls className="mb-6">
                      <source src="/audio/prshikshankaryakram.mp3" type="audio/mp3" />

                   आपका ब्राउज़र ऑडियो को सपोर्ट नहीं करता।
                   </audio> 
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{
                y: -10,
                scale: 1.03,
                backgroundColor: "#f0fdf4"
              }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-200"
            >
              <div className="h-48 bg-green-600 flex items-center justify-center relative overflow-hidden">
                <img
                  src={about3}
                  alt="कृषि उपकरण"
                  className="w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-800/70 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">तकनीकी सहायता</h3>
                <p className="text-gray-600">
                  हम किसानों को कम दाम में अच्छे कृषि उपकरण उपलब्ध कराते हैं। मृदा परीक्षण किट, ड्रिप इरिगेशन सिस्टम, स्प्रेयर जैसे उपकरणों के बारे में जानकारी और सब्सिडी पाने में मदद करते हैं।
                </p>
              </div>
              <audio controls className="mb-6">
                      <source src="/audio/ts.mp3" type="audio/mp3" />

                   आपका ब्राउज़र ऑडियो को सपोर्ट नहीं करता।
                   </audio> 
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{
                y: -10,
                scale: 1.03,
                backgroundColor: "#f0fdf4"
              }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-200"
            >
              <div className="h-48 bg-green-600 flex items-center justify-center relative overflow-hidden">
                <img
                  src={about5}
                  alt="फसल बिक्री"
                  className="w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-800/70 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">बाजार संपर्क</h3>
                <p className="text-gray-600">
                  हम किसानों को सीधे बाजार से जोड़ते हैं ताकि उन्हें अपनी फसल का सही दाम मिल सके। FPO बनाने में मदद, ऑनलाइन मंडी से जोड़ना और सीधे कंपनियों को बेचने के अवसर देना - ये सब हम करते हैं।
                </p>
              </div>
              <audio controls className="mb-6">
                      <source src="/audio/bazar.mp3" type="audio/mp3" />

                   आपका ब्राउज़र ऑडियो को सपोर्ट नहीं करता।
                   </audio> 
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">अपनी खेती को बदलने के लिए तैयार?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              हजारों किसानों में शामिल हों जिन्होंने Unnatkheti की मदद से अपनी आमदनी बढ़ाई है। हमारे विशेषज्ञ आपको निःशुल्क सलाह देंगे।
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-green-700 font-bold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                विशेषज्ञ से बात करें
              </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;